import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createBlock as _createBlock, resolveDirective as _resolveDirective, withDirectives as _withDirectives, withCtx as _withCtx, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-14100edf"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "core-data-trend" }
const _hoisted_2 = { class: "trend-date" }
const _hoisted_3 = {
  key: 0,
  class: "trend-grid"
}

import { computed, ref, watch } from 'vue'
import dayjs from 'dayjs'
import { EChartsOption } from 'echarts'
import BaseEchart from '@/components/BaseEchart.vue'
import { secondToString } from '@/utils/date-format'
import {
  CoreDataTrendItem,
  getCoreDataTrend
} from '@/service/main/coreData'

interface TrendSeriesConfig {
  name: string
  field: keyof CoreDataTrendItem
  color: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CoreDataTrend',
  props: {
    visible: { type: Boolean },
    employee: {}
  },
  emits: ["update:visible"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const endDate = dayjs().subtract(1, 'day').endOf('day')
const startDate = endDate.subtract(2, 'month').add(1, 'day').startOf('day')
const loading = ref(false)
const trendData = ref<CoreDataTrendItem[]>([])
const dialogVisible = computed({
  get: () => props.visible,
  set: (value: boolean) => emit('update:visible', value)
})
const currentEmployeeData = computed(() => trendData.value)

const formatNumber = (value: number) =>
  new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 2 }).format(value)

const createChartOption = (
  seriesConfigs: TrendSeriesConfig[],
  valueUnit: 'hour' | 'count' | 'amount'
): EChartsOption => {
  const dates = currentEmployeeData.value.map((item) =>
    dayjs(item.date).format('MM-DD')
  )
  const getValue = (item: CoreDataTrendItem, field: keyof CoreDataTrendItem) => {
    const value = Number(item[field])
    return valueUnit === 'hour' ? Number((value / 3600).toFixed(2)) : value
  }
  const unit = valueUnit === 'hour' ? '小时' : valueUnit === 'amount' ? '元' : ''
  const tooltip =
    valueUnit === 'hour'
      ? {
          trigger: 'axis' as const,
          formatter: (params: any) => {
            const item = Array.isArray(params) ? params[0] : params
            const duration = Number(
              currentEmployeeData.value[item?.dataIndex]?.callDuration || 0
            )
            const date = item?.axisValueLabel || item?.axisValue || ''

            return `${date}<br/>${item?.marker || ''}${
              item?.seriesName || '通话时长'
            }&nbsp;&nbsp;<strong>${secondToString(duration)}</strong>`
          }
        }
      : {
          trigger: 'axis' as const,
          valueFormatter: (value: unknown) =>
            `${formatNumber(Number(value))}${unit}`
        }

  return {
    color: seriesConfigs.map((item) => item.color),
    tooltip,
    legend: seriesConfigs.length > 1 ? { top: 0 } : undefined,
    grid: {
      top: seriesConfigs.length > 1 ? 46 : 22,
      right: 24,
      bottom: 40,
      left: 58,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: dates,
      axisLabel: { color: '#606266' },
      axisLine: { lineStyle: { color: '#dcdfe6' } }
    },
    yAxis: {
      type: 'value',
      minInterval: valueUnit === 'count' ? 1 : undefined,
      axisLabel: {
        color: '#606266',
        formatter: (value: number) =>
          valueUnit === 'amount' && value >= 10000
            ? `${value / 10000}万`
            : `${value}`
      },
      splitLine: { lineStyle: { color: '#ebeef5', type: 'dashed' } }
    },
    dataZoom: [{ type: 'inside', start: 0, end: 100 }],
    series: seriesConfigs.map((config) => ({
      name: config.name,
      type: 'line',
      showSymbol: false,
      emphasis: { focus: 'series' },
      lineStyle: { width: 2 },
      data: currentEmployeeData.value.map((item) =>
        getValue(item, config.field)
      )
    }))
  }
}

const charts = computed(() => [
  {
    title: '通话时长（小时）',
    option: createChartOption(
      [{ name: '通话时长', field: 'callDuration', color: '#337ecc' }],
      'hour'
    )
  },
  {
    title: '通话次数',
    option: createChartOption(
      [{ name: '通话次数', field: 'callCount', color: '#00a870' }],
      'count'
    )
  },
  {
    title: '直播&录播数据趋势（直播数据/培养课录播数据/营销课录播数据）',
    option: createChartOption(
      [
        { name: '直播数据', field: 'liveData', color: '#337ecc' },
        { name: '培养课录播数据', field: 'peiyangFbData', color: '#00a870' },
        { name: '营销课录播数据', field: 'yingxiaoFbData', color: '#e6a23c' }
      ],
      'count'
    )
  },
  {
    title: '成交金额（元）',
    option: createChartOption(
      [{ name: '成交金额', field: 'performanceAmount', color: '#d54941' }],
      'amount'
    )
  },
  {
    title: '成交单数',
    option: createChartOption(
      [{ name: '成交单数', field: 'performanceCount', color: '#7b61a8' }],
      'count'
    )
  }
])

const loadTrendData = async () => {
  loading.value = true
  trendData.value = []
  try {
    trendData.value = await getCoreDataTrend({
      startTime: startDate.format('YYYY-MM-DD HH:mm:ss'),
      endTime: endDate.format('YYYY-MM-DD HH:mm:ss'),
      empId: props.employee.empId!
    })
  } finally {
    loading.value = false
  }
}

watch(
  () => props.visible,
  (visible) => {
    if (visible) loadTrendData()
  }
)

return (_ctx: any,_cache: any) => {
  const _component_el_empty = _resolveComponent("el-empty")!
  const _component_el_dialog = _resolveComponent("el-dialog")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createBlock(_component_el_dialog, {
    modelValue: dialogVisible.value,
    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((dialogVisible).value = $event)),
    title: `业务员趋势数据：${_ctx.employee.empName || ''}`,
    width: "90%",
    top: "4vh",
    "append-to-body": ""
  }, {
    default: _withCtx(() => [
      _withDirectives((_openBlock(), _createElementBlock("section", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, _toDisplayString(_unref(startDate).format('YYYY-MM-DD')) + " 至 " + _toDisplayString(_unref(endDate).format('YYYY-MM-DD')), 1),
        (currentEmployeeData.value.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(charts.value, (chart) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: chart.title,
                  class: "trend-chart"
                }, [
                  _createElementVNode("h3", null, _toDisplayString(chart.title), 1),
                  _createVNode(BaseEchart, {
                    options: chart.option,
                    height: "300px"
                  }, null, 8, ["options"])
                ]))
              }), 128))
            ]))
          : (!loading.value)
            ? (_openBlock(), _createBlock(_component_el_empty, {
                key: 1,
                description: "暂无趋势数据"
              }))
            : _createCommentVNode("", true)
      ])), [
        [_directive_loading, loading.value]
      ])
    ]),
    _: 1
  }, 8, ["modelValue", "title"]))
}
}

})